import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Sparkles, ArrowRight, Shield, Globe, Award } from 'lucide-react';

const Logo = () => (
  <div className="flex items-baseline gap-1">
    <span className="font-serif font-normal tracking-tight text-stone-900 dark:text-white text-2xl">Cited</span>
    <span className="font-serif font-normal italic text-stone-500 dark:text-stone-400 text-2xl">AI</span>
  </div>
);

export default function LoginPage() {
  const { login, user } = useAuth();

  // If already logged in, redirect
  React.useEffect(() => {
    if (user) {
      window.location.href = '/dashboard';
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-stone-900 p-12 flex-col justify-between">
        <Logo />
        <div>
          <h2 className="font-serif text-4xl text-white mb-6">Get your brand cited by AI</h2>
          <p className="text-stone-400 text-lg leading-relaxed mb-8">
            Track, monitor, and optimize your visibility across ChatGPT, Perplexity, Claude, Gemini, and Google AI Overviews.
          </p>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-sm text-stone-400">
              <Shield className="w-4 h-4" /> SOC 2 Compliant
            </div>
            <div className="flex items-center gap-2 text-sm text-stone-400">
              <Globe className="w-4 h-4" /> 5 LLMs tracked
            </div>
            <div className="flex items-center gap-2 text-sm text-stone-400">
              <Award className="w-4 h-4" /> 500+ brands
            </div>
          </div>
        </div>
        <p className="text-stone-600 text-sm">&copy; 2026 CitedAI. All rights reserved.</p>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo />
          </div>
          
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-sm mb-4">
              <Sparkles className="w-4 h-4" /> Welcome to CitedAI
            </div>
            <h1 className="font-serif text-3xl text-stone-900 dark:text-white mb-2">Sign in to your account</h1>
            <p className="text-stone-500">Start tracking your AI visibility</p>
          </div>

          <button
            onClick={login}
            className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-stone-900 text-white hover:bg-stone-800 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-100 font-medium transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </button>

          <p className="text-center text-sm text-stone-500 mt-6">
            By signing in, you agree to our{' '}
            <a href="#" className="underline hover:text-stone-900 dark:hover:text-white">Terms</a>{' '}
            and{' '}
            <a href="#" className="underline hover:text-stone-900 dark:hover:text-white">Privacy Policy</a>
          </p>

          <div className="mt-8 pt-8 border-t border-stone-200 dark:border-stone-800 text-center">
            <Link to="/" className="text-sm text-stone-500 hover:text-stone-900 dark:hover:text-white flex items-center justify-center gap-1">
              <ArrowRight className="w-4 h-4 rotate-180" /> Back to homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
